import { css } from '@emotion/react';
import {
  faAngleDoubleDown,
  faCog, faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useCallback, useMemo,
} from 'react';
import { RegularDropdownComponent } from '~/_shared/baseComponents/dropdown';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { InputSize } from '~/_shared/baseComponents/inputs';
import {
  hexAndOpacityToRGBA,
  rgbColorToString,
} from '~/_shared/components/colorPicker/colorPicker.helpers';
import { HoverableIconComponent } from '~/_shared/components/icons/hoverableIcon.component';
import { useTheme } from '~/_shared/themes/theme.hooks';
import type { Theme } from '~/_shared/themes/theme.model';
import {
  type GroupingColumn, type GroupingColumnNumeric, GroupingType,
} from '~/_shared/types/grouping/grouping';
import { type SpreadsheetColumn } from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import { useTranslation } from '~/_shared/utils/hooks';
import { useSlidingAnimationMsDuration } from '~/store/frontendState/graphicSettings/graphicSettings.selectors';
import { type GroupChangeRequest } from '../useGroupColumnData';

export type GroupingSettingColumnProps = Readonly<{
  columns: SpreadsheetColumn[];
  isExpanded: boolean;
  selected: GroupingColumn | null;

  onChange: (columnChange: GroupChangeRequest) => void;
  onGroupColumnRemove?: (column: GroupingColumn) => void;
  onIsExpandedChange: (isExpanded: boolean) => void;
  onNumericGroupColumnSettingsClick?: (column: GroupingColumnNumeric) => void;
}>;

const optionContainerStyle = css({
  marginBottom: '8px',
});

const animatedPanelStyle = ({ isOpen, durationMs }: { isOpen: boolean; durationMs: number }) => (theme: Theme) => css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  backgroundColor: theme.backgroundColors.secondary,
  borderRadius: 4,
  boxSizing: 'border-box',
  padding: 4,
  width: '100%',
  overflow: 'hidden',
  height: isOpen ? 76 : 40,
  willChange: 'height',
  transition: `height ${durationMs}ms`,
});

const mainPanelStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});

const dropdownStyle = css({
  flex: 1,
  minWidth: 0,
});

const chevronStyle = ({ isDisabled, areOptionsShown }: { isDisabled: boolean; areOptionsShown: boolean }) => (theme: Theme) => css({
  color: isDisabled ? theme.textColors.disabled : 'inherit',
  cursor: isDisabled ? 'not-allowed' : 'default',
  margin: '0 8px',
  transform: areOptionsShown ? 'rotate(-180deg)' : undefined,
  transition: 'transform .2s',
  '&:hover': {
    color: isDisabled ? theme.textColors.disabled : theme.iconColors.primary,
  },
});

const optionsPanelStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '4px 8px',
});

const toggleStyle = css({
  display: 'flex',
  cursor: 'default',
  fontSize: 12,
  textTransform: 'uppercase',
});

const toggleItemStyle = ({ isActive }: { isActive: boolean }) => (theme: Theme) => {
  const activeBackgroundColor = hexAndOpacityToRGBA(theme.buttonColors.primaryBackground, 0.7);

  return css({
    borderRadius: 4,
    padding: 4,
    color: isActive ? theme.textColors.quaternary : theme.textColors.primary,
    textAlign: 'center',
    ...(isActive && { backgroundColor: rgbColorToString(activeBackgroundColor) }),
    '&:hover': {
      ...(!isActive && { color: theme.textColors.linkOnHover }),
    },
  });
};

const toggleDividerStyle = css({
  margin: '0 6px',
  padding: '4px 0',
});

const iconOptionStyle = (theme: Theme) => css({
  color: theme.iconColors.highContrast,
  marginRight: 12,
  '&:hover': {
    color: theme.iconColors.primary,
  },
});

const trashIconStyle = css({
  background: 'none',
  border: 'none',
  color: 'inherit',
  fontSize: '16px',
  padding: 0,
});

const DEFAULT_TYPE = GroupingType.Text;

export const GroupingSettingColumn: FC<GroupingSettingColumnProps> = props => {
  const slidingAnimationDurationMs = useSlidingAnimationMsDuration();
  const [t] = useTranslation();
  const theme = useTheme();

  const { onChange, selected, columns, onNumericGroupColumnSettingsClick, onGroupColumnRemove, onIsExpandedChange, isExpanded } = props;

  const onChangeSource = useCallback((columnId: string) => {
    const option = columns.find(item => item.id === columnId);

    if (!option) {
      return;
    }

    onChange({
      columnId,
      type: selected?.type ?? DEFAULT_TYPE,
      spreadsheetId: option.spreadsheetId,
    });
  }, [columns, onChange, selected?.type]);

  const onChangeType = useCallback((type: GroupingType) => () =>
    selected && onChange({
      columnId: selected.columnId,
      type,
      spreadsheetId: selected.spreadsheetId,
    }), [onChange, selected]);

  const toggleShowDetailsClick = useCallback(() => {
    if (selected) {
      onIsExpandedChange(!isExpanded);
    }
  }, [selected, isExpanded, onIsExpandedChange]);

  const handleClickNumericSettings = useCallback(() => {
    if (onNumericGroupColumnSettingsClick && selected && selected.type === GroupingType.Numeric) {
      onNumericGroupColumnSettingsClick(selected);
    }
  }, [onNumericGroupColumnSettingsClick, selected]);

  const handleRemoveColumn = useMemo(() =>
    onGroupColumnRemove && selected ? () => onGroupColumnRemove(selected) : undefined
  , [onGroupColumnRemove, selected]);

  return (
    <div css={optionContainerStyle}>
      <div
        css={animatedPanelStyle({ isOpen: isExpanded && !!selected, durationMs: slidingAnimationDurationMs })}
      >
        <div css={mainPanelStyle} >
          <RegularDropdownComponent
            css={dropdownStyle}
            inPortal
            inputSize={InputSize.Small}
            onChange={onChangeSource}
            options={columns.map(c => ({ name: c.name, value: c.id }))}
            placeholder={t('No Group Selected')}
            value={selected?.columnId}
            testid="groupingDropdown"
          />
          <FontAwesomeIcon
            onClick={toggleShowDetailsClick}
            title={t('Toggle Grouping Details')}
            icon={faAngleDoubleDown}
            css={chevronStyle({ isDisabled: !selected, areOptionsShown: isExpanded })}
          />
        </div>

        {selected && (
          <div css={optionsPanelStyle}>
            <div css={toggleStyle}>
              <div
                onClick={onChangeType(GroupingType.Text)}
                css={toggleItemStyle({
                  isActive: selected?.type === GroupingType.Text,
                })}
              >
                {t('Text')}
              </div>
              <div css={toggleDividerStyle}> /</div>
              <div
                onClick={onChangeType(GroupingType.Numeric)}
                css={toggleItemStyle({ isActive: selected?.type === GroupingType.Numeric })}
              >
                {t('Numeric')}
              </div>
            </div>

            <div>
              {selected.type === GroupingType.Numeric && (
                <FontAwesomeIcon
                  css={iconOptionStyle}
                  icon={faCog}
                  onClick={handleClickNumericSettings}
                  title={`${t('Numeric')} ${t('Settings')}`}
                />
              )}
              {handleRemoveColumn && (
                <button
                  css={trashIconStyle}
                  onClick={handleRemoveColumn}
                >
                  <HoverableIconComponent
                    hoverColor={theme.iconColors.danger}
                    icon={faTrash}
                    data-testid="remove-grouping-column"
                  />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
